<template>
    <div class="alertDetailModal">
        <TabsComponent
            :tabs="alertTabs"
            default-tab="settings"
            @tab-change="onTabChange"
        >
            <!-- Tab 1: Documento -->
            <template #document>
                <div class="tab_content">
                    <div class="modal-section">
                        <h4>Informações do Documento</h4>
                        <div class="document_card">
                            <div class="document_icon">
                                <sw-icon :name="getDocumentIcon(form.documentType)" />
                            </div>
                            <div class="document_details">
                                <h5 class="document_name">{{ form.documentName }}</h5>
                                <span class="document_type">{{ form.documentType }}</span>
                                <span class="document_expiry" :class="getExpiryClass()">
                                    {{ formatExpiryDate() }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="modal-section">
                        <h4>Informações Adicionais</h4>
                        <div class="document_meta">
                            <div class="meta_item">
                                <span class="meta_label">Próxima notificação:</span>
                                <span class="meta_value">
                                    {{ formatNextNotification() }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Tab 2: Configurações -->
            <template #settings>
                <div class="tab_content">
                    <div class="modal-section">
                        <h4>Informações Básicas</h4>
                        <div class="form_field">
                            <sw-input
                                v-model="form.name"
                                label="Nome do Alerta"
                                placeholder="Ex: CNH - João Silva Santos"
                                icon="pencil"
                            />
                        </div>
                    </div>

                    <div class="modal-section">
                        <h4>Avisar com antecedência de:</h4>
                        <div class="form_field">
                            <div class="trigger_days">
                                <div
                                    v-for="day in availableTriggerDays"
                                    :key="day"
                                    class="trigger_day"
                                    :class="{ active: form.triggerDays.includes(day) }"
                                    @click="toggleTriggerDay(day)"
                                >
                                    <span class="day_number">{{ day }}</span>
                                    <span class="day_label">{{ day === 1 ? 'dia' : 'dias' }}</span>
                                </div>
                            </div>
                            <p class="help_text">Selecione quando deseja ser notificado antes do vencimento</p>
                        </div>
                    </div>

                    <div class="modal-section">
                        <h4>Horário para envio</h4>
                        <div class="form_field">
                            <sw-input
                                v-model="form.preferredTime"
                                label="Horário para envio"
                                icon="clock"
                                mask="##:##"
                                placeholder="09:00"
                            />
                            <p class="help_text">Horário em que os alertas serão enviados (formato: HH:MM)</p>
                        </div>
                    </div>

                </div>
            </template>

            <!-- Tab 3: Destinatários -->
            <template #recipients>
                <div class="tab_content">
                    <div class="modal-section">
                        <h4>Emails para receber alertas</h4>
                        <div class="form_field">
                            <sw-tag
                                v-model="form.recipients"
                                placeholder="Digite um email e pressione Enter"
                                icon="mail"
                                color="blue"
                                validation-type="email"
                            />
                            <p class="help_text">Digite os emails que receberão as notificações (pressione Enter após cada email)</p>
                        </div>
                    </div>

                    <div class="modal-section">
                        <h4>Mensagem Personalizada</h4>
                        <div class="form_field">
                            <QuillEditor
                                v-model="form.customMessage"
                                placeholder="Digite uma mensagem personalizada que será incluída no alerta..."
                            />
                            <p class="help_text">Máximo 500 caracteres</p>
                        </div>
                    </div>
                </div>
            </template>

        </TabsComponent>

        <!-- Ações do Modal -->
        <div class="modal_actions">
            <sw-button
                name="Salvar Alterações"
                color="blue"
                @click="handleSave"
                :disabled="!isFormValid"
            />
            <sw-button
                name="Cancelar"
                color="gray"
                @click="handleCancel"
            />
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';
    import TabsComponent from '@Component/tabs/TabsComponent.vue';
    import QuillEditor from '@Component/forms/QuillEditor.vue';
    import {
        ALERT_STATUS,
        ALERT_STATUS_LABELS,
        ALERT_TRIGGER_DAYS,
        FILE_ICONS
    } from '@/constants';

    // Props
    const props = defineProps({
        alertData: {
            type: Object,
            required: true
        }
    });

    // Emits
    const emit = defineEmits(['close', 'save']);

    // Estado do formulário
    const form = ref({
        id: '',
        name: '',
        documentId: '',
        documentType: '',
        documentName: '',
        expiryDate: null,
        triggerDays: [30, 15, 7, 3, 1],
        recipients: [],
        customMessage: '',
        preferredTime: '09:00',
        status: ALERT_STATUS.ACTIVE,
        urgencyLevel: 'safe',
        nextNotification: null,
        lastNotification: null,
        createdAt: null,
        updatedAt: null
    });

    // Tabs do alerta
    const alertTabs = ref([
        { id: 'settings', label: 'Configurações', icon: 'cog' },
        { id: 'document', label: 'Documento', icon: 'file-text' },
        { id: 'recipients', label: 'Destinatários', icon: 'mail' }
    ]);

    // Computed
    const availableTriggerDays = computed(() => ALERT_TRIGGER_DAYS);

    const isFormValid = computed(() => {
        return form.value.name.trim() !== '' &&
               form.value.triggerDays.length > 0 &&
               form.value.recipients.length > 0;
    });

    // Methods
    const getDocumentIcon = (documentType) => {
        return FILE_ICONS[documentType] || 'file-text';
    };

    const getExpiryClass = () => {
        if (!form.value.expiryDate) return 'no-expiry';
        const date = new Date(form.value.expiryDate);
        const now = new Date();
        const diffTime = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 30) return 'expiry-critical';
        if (diffDays < 60) return 'expiry-warning';
        return 'expiry-safe';
    };

    const formatExpiryDate = () => {
        if (!form.value.expiryDate) return 'Sem vencimento';
        const date = new Date(form.value.expiryDate);
        const now = new Date();
        const diffTime = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return `Vencido há ${Math.abs(diffDays)} dias`;
        } else if (diffDays === 0) {
            return 'Vence hoje';
        } else if (diffDays === 1) {
            return 'Vence amanhã';
        } else {
            return `Vence em ${diffDays} dias`;
        }
    };


    const formatNextNotification = () => {
        if (!form.value.nextNotification) return 'Não agendada';
        return formatDate(form.value.nextNotification);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    const toggleTriggerDay = (day) => {
        const index = form.value.triggerDays.indexOf(day);
        if (index > -1) {
            form.value.triggerDays.splice(index, 1);
        } else {
            form.value.triggerDays.push(day);
            form.value.triggerDays.sort((a, b) => b - a);
        }
    };


    const onTabChange = (tabId) => {
        console.log('Tab changed to:', tabId);
    };

    const handleSave = () => {
        if (isFormValid.value) {
            emit('save', form.value);
        }
    };

    const handleCancel = () => {
        emit('close');
    };

    // Lifecycle
    onMounted(() => {
        if (props.alertData) {
            form.value = { ...form.value, ...props.alertData };
        }
    });
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    .alertDetailModal {
        display: flex;
        flex-direction: column;
        gap: 1.5em;
    }

    // ===== TAB CONTENT =====
    .tab_content {
        display: flex;
        flex-direction: column;
        gap: 1.5em;
        padding: 1.5em 0;
    }

    .tab_header {
        text-align: center;
        border-bottom: 1px solid var(--borderColorLight);
        padding-bottom: 1em;

        h4 {
            font-size: 1.375em;
            font-weight: 700;
            color: var(--textPrimary);
            margin: 0 0 0.5em 0;
        }

        p {
            font-size: 0.9375em;
            color: var(--textSecondary);
            margin: 0;
        }
    }

    // ===== DOCUMENT TAB =====
    .document_info {
        display: flex;
        flex-direction: column;
        gap: 1.5em;
    }

    .document_card {
        display: flex;
        align-items: center;
        gap: 1.25em;
        padding: 1.5em;
        background: rgba(59, 130, 246, 0.05);
        border: 1px solid rgba(59, 130, 246, 0.1);
        border-radius: 1em;

        .document_icon {
            width: 4em;
            height: 4em;
            border-radius: 0.75em;
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5em;
            flex-shrink: 0;
        }

        .document_details {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 0.25em;

            .document_name {
                font-size: 1.125em;
                font-weight: 700;
                color: var(--textPrimary);
                margin: 0;
            }

            .document_type {
                font-size: 0.875em;
                color: var(--textSecondary);
                font-weight: 500;
            }

            .document_expiry {
                font-size: 0.875em;
                font-weight: 600;
                padding: 0.375em 0.75em;
                border-radius: 1em;
                align-self: flex-start;
                margin-top: 0.5em;

                &.expiry-safe {
                    background: rgba(16, 185, 129, 0.1);
                    color: #059669;
                }

                &.expiry-warning {
                    background: rgba(245, 158, 11, 0.1);
                    color: #d97706;
                }

                &.expiry-critical {
                    background: rgba(239, 68, 68, 0.1);
                    color: #dc2626;
                }

                &.no-expiry {
                    background: rgba(156, 163, 175, 0.1);
                    color: #6b7280;
                }
            }
        }
    }

    .document_meta {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1em;

        .meta_item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1em;
            background: white;
            border: 1px solid var(--borderColorLight);
            border-radius: 0.5em;

            .meta_label {
                font-size: 0.875em;
                color: var(--textSecondary);
                font-weight: 500;
            }

            .meta_value {
                font-size: 0.875em;
                font-weight: 600;
                color: var(--textPrimary);

                &.status-active {
                    color: #16a34a;
                }

                &.status-inactive {
                    color: #dc2626;
                }

                &.urgency-safe {
                    color: #16a34a;
                }

                &.urgency-warning {
                    color: #d97706;
                }

                &.urgency-critical {
                    color: #dc2626;
                }
            }
        }
    }

    // ===== SETTINGS TAB =====
    .settings_form {
        display: flex;
        flex-direction: column;
        gap: 1.5em;
    }

    .form_field {
        margin-bottom: 1.5em;

        &:last-child {
            margin-bottom: 0;
        }

        .field_label {
            display: block;
            font-size: 0.875em;
            font-weight: 600;
            color: var(--textPrimary);
            margin-bottom: 0.75em;
        }

        .help_text {
            font-size: 0.8125em;
            color: var(--textSecondary);
            margin-top: 0.5em;
            margin-bottom: 0;
        }
    }

    .trigger_days {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
        gap: 0.75em;
        margin-bottom: 0.5em;

        .trigger_day {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.25em;
            padding: 1em 0.75em;
            border: 2px solid var(--borderColorLight);
            border-radius: 0.75em;
            cursor: pointer;
            transition: all 0.2s ease;
            background: white;

            &:hover {
                border-color: rgba(59, 130, 246, 0.3);
                transform: translateY(-1px);
            }

            &.active {
                border-color: #3b82f6;
                background: rgba(59, 130, 246, 0.05);

                .day_number {
                    color: #3b82f6;
                }
            }

            .day_number {
                font-size: 1.25em;
                font-weight: 700;
                color: var(--textPrimary);
                transition: color 0.2s ease;
            }

            .day_label {
                font-size: 0.75em;
                color: var(--textSecondary);
                font-weight: 500;
            }
        }
    }


    // ===== RECIPIENTS TAB =====
    .recipients_form {
        display: flex;
        flex-direction: column;
        gap: 1.5em;
    }

    .custom_message {
        width: 100%;
        padding: 0.875em 1em;
        border: 1px solid var(--borderColorLight);
        border-radius: 0.5em;
        font-family: inherit;
        font-size: 0.875em;
        line-height: 1.5;
        color: var(--textPrimary);
        background: white;
        resize: vertical;
        min-height: 4em;
        transition: border-color 0.2s ease;

        &:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        &::placeholder {
            color: var(--placeholderColor, #9ca3af);
        }
    }



    // ===== MODAL ACTIONS =====
    .modal_actions {
        display: flex;
        gap: 1em;
        justify-content: flex-end;
        padding-top: 1em;
        border-top: 1px solid var(--borderColorLight);
        margin-top: 1em;
    }

    // ===== RESPONSIVIDADE =====
    @include screen_medias(s) {
        .alertDetailModal {
            .document_card {
                flex-direction: column;
                text-align: center;
                gap: 1em;

                .document_details {
                    align-items: center;

                    .document_expiry {
                        align-self: center;
                    }
                }
            }

            .document_meta {
                grid-template-columns: 1fr;

                .meta_item {
                    flex-direction: column;
                    gap: 0.5em;
                    text-align: center;
                }
            }

            .trigger_days {
                grid-template-columns: repeat(3, 1fr);
            }

            .modal_actions {
                flex-direction: column;
                gap: 0.75em;
            }

        }
    }
</style>